import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  InputAdornment,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Tooltip,
  Avatar,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import { useRealEstate } from '../../contexts/RealEstateContext';

const BuyerList = () => {
  const navigate = useNavigate();
  const { getAllBuyers, getBuyerViewings } = useRealEstate();
  const buyers = getAllBuyers();

  const [searchTerm, setSearchTerm] = useState('');
  const [approvalFilter, setApprovalFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Filter buyers
  const filteredBuyers = useMemo(() => {
    return buyers.filter((buyer) => {
      const matchesSearch =
        buyer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        buyer.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        buyer.phone.includes(searchTerm) ||
        buyer.preferredLocations.some((loc) =>
          loc.toLowerCase().includes(searchTerm.toLowerCase())
        );

      const matchesApproval =
        approvalFilter === 'all' ||
        (approvalFilter === 'approved' && buyer.preApproved) ||
        (approvalFilter === 'pending' && !buyer.preApproved);

      return matchesSearch && matchesApproval;
    });
  }, [buyers, searchTerm, approvalFilter]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedBuyers = filteredBuyers.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const formatPrice = (price) => {
    return `$${(price / 1000).toFixed(0)}K`;
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight={700}>
          All Buyers
        </Typography>
        <Typography variant="body2" color="text.secondary">
          View and manage buyer contacts
        </Typography>
      </Box>

      {/* Filters */}
      <Card
        elevation={0}
        sx={{
          mb: 3,
          border: '1px solid',
          borderColor: 'grey.200',
          borderRadius: 2,
        }}
      >
        <CardContent sx={{ p: 2.5 }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField
              size="small"
              placeholder="Search by name, email, phone, or location..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ flex: 1, minWidth: 250 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" sx={{ fontSize: 20 }} />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchTerm('')}>
                      <CloseIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Pre-Approval Status</InputLabel>
              <Select
                value={approvalFilter}
                label="Pre-Approval Status"
                onChange={(e) => setApprovalFilter(e.target.value)}
              >
                <MenuItem value="all">All Buyers</MenuItem>
                <MenuItem value="approved">Pre-Approved</MenuItem>
                <MenuItem value="pending">Pending Approval</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </CardContent>
      </Card>

      {/* Results count */}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Showing {filteredBuyers.length} of {buyers.length} buyers
      </Typography>

      {/* Buyers Table */}
      <Card
        elevation={0}
        sx={{
          border: '1px solid',
          borderColor: 'grey.200',
          borderRadius: 2,
        }}
      >
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'grey.50' }}>
                <TableCell sx={{ fontWeight: 600 }}>Buyer</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Contact</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Price Range</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Requirements</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Pre-Approval</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Viewings</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedBuyers.map((buyer) => {
                const viewingsCount = getBuyerViewings(buyer.id).length;
                return (
                  <TableRow
                    key={buyer.id}
                    sx={{
                      '&:hover': {
                        backgroundColor: 'grey.50',
                      },
                    }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar
                          sx={{
                            width: 36,
                            height: 36,
                            backgroundColor: 'grey.200',
                            color: 'text.primary',
                            fontSize: '0.875rem',
                            fontWeight: 600,
                          }}
                        >
                          {buyer.name.split(' ').map((n) => n[0]).join('')}
                        </Avatar>
                        <Typography variant="body2" fontWeight={500}>
                          {buyer.name}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2">
                          {buyer.email}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {buyer.phone}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {formatPrice(buyer.priceRange.min)} - {formatPrice(buyer.priceRange.max)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {buyer.bedrooms} bed, {buyer.bathrooms}+ bath
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                        {buyer.preferredLocations.slice(0, 2).map((loc) => (
                          <Chip
                            key={loc}
                            label={loc}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: '0.65rem', height: 20 }}
                          />
                        ))}
                        {buyer.preferredLocations.length > 2 && (
                          <Chip
                            label={`+${buyer.preferredLocations.length - 2}`}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: '0.65rem', height: 20 }}
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {buyer.preApproved ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Chip
                            icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
                            label="Approved"
                            size="small"
                            color="success"
                            sx={{ fontWeight: 500 }}
                          />
                          <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                            ${buyer.preApprovalAmount?.toLocaleString()}
                          </Typography>
                        </Box>
                      ) : (
                        <Chip
                          icon={<WarningIcon sx={{ fontSize: 14 }} />}
                          label="Pending"
                          size="small"
                          color="warning"
                          sx={{ fontWeight: 500 }}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {viewingsCount} {viewingsCount === 1 ? 'property' : 'properties'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/real-estate/buyers/${buyer.id}`)}
                        >
                          <VisibilityIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
              {paginatedBuyers.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="body1" color="text.secondary">
                        No buyers found matching your criteria.
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filteredBuyers.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25]}
        />
      </Card>
    </Box>
  );
};

export default BuyerList;
